import { createContext, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";

export const FALLBACK_SETTINGS = {
  company_name: "Travelrushh International Tourism",
  tagline: "Crafted journeys. Timeless memories.",
  phone: "7028123333",
  email: "travelrushpune@gmail.com",
  address: "Office no. 106, Aishwaryam deerstone, near Mhalsakant School, Akurdi, Pimpri-Chinchwad, Maharashtra 411035",
  whatsapp: "7028123333",
  logo_url: null,
  stat_years: 10,
  stat_countries: 75,
  stat_travellers: 50000,
  stat_experts: 25,
  stat_visa_success: "99.4%",
  color_bg: "#0A1C30",
  color_surface: "#10263F",
  color_card: "#162F4E",
  color_accent: "#F5A300",
  color_text: "#FDFBF7",
  dmc_hub_url: "https://dmchub.emergent.host",
  why_1_title: "Itineraries tailored to the discerning traveller",
  why_1_desc: "No off-the-shelf packages. Every route, hotel and table is chosen around your pace, palate and curiosity.",
  why_2_title: "A 99.4% visa approval track record",
  why_2_desc: "Dedicated visa desks for Schengen, USA, UAE and 40+ countries — documentation audited twice before filing.",
  why_3_title: "Private chauffeurs & heritage stays",
  why_3_desc: "Hand-picked 5-star and boutique heritage properties, with drivers who double up as local storytellers.",
  why_4_title: "Concierge access, before to beyond",
  why_4_desc: "One direct line to your travel designer — before you book, while you travel, and after you return.",
};

const hexToRgbTriplet = (hex) => {
  const m = /^#?([0-9a-f]{6})$/i.exec((hex || "").trim());
  if (!m) return null;
  const n = parseInt(m[1], 16);
  return `${(n >> 16) & 255} ${(n >> 8) & 255} ${n & 255}`;
};

const applyTheme = (s) => {
  const root = document.documentElement;
  const map = {
    color_bg: "--c-ink",
    color_surface: "--c-surface",
    color_card: "--c-card",
    color_accent: "--c-gold",
    color_text: "--c-cream",
  };
  Object.entries(map).forEach(([key, cssVar]) => {
    const triplet = hexToRgbTriplet(s[key]);
    if (triplet) root.style.setProperty(cssVar, triplet);
  });
};

const SettingsContext = createContext(FALLBACK_SETTINGS);

export const useSettings = () => useContext(SettingsContext);

export function SettingsProvider({ children }) {
  const [settings, setSettings] = useState(FALLBACK_SETTINGS);

  useEffect(() => {
    let alive = true;
    const load = (attempt = 0) =>
      api
        .get("/settings")
        .then((res) => {
          if (!alive) return;
          const merged = { ...FALLBACK_SETTINGS, ...res.data };
          setSettings(merged);
          applyTheme(merged);
          // keep the browser tab title + description in sync with admin branding
          document.title = `${merged.company_name} | Domestic & International Tours, Visa Services`;
          const meta = document.querySelector('meta[name="description"]');
          if (meta) meta.setAttribute("content", `${merged.company_name} — domestic & international tour packages and visa services`);
        })
        .catch(() => {
          // retry with backoff — a slow first response must not leave the site on fallback branding
          if (alive && attempt < 2) {
            setTimeout(() => load(attempt + 1), 1500 * (attempt + 1));
          }
        });
    load();
    // pick up admin-panel saves when the user returns to the tab (debounced single handler)
    let lastFetch = 0;
    const onFocus = () => {
      const now = Date.now();
      if (now - lastFetch < 2000) return;
      lastFetch = now;
      load();
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    return () => {
      alive = false;
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
    };
  }, []);

  return <SettingsContext.Provider value={settings}>{children}</SettingsContext.Provider>;
}
